import { type ChangeEvent, type ReactNode, useState } from "react";
import { useNavigate } from "react-router";

import { ApiError, isError, isSuccess } from "@/api/ApiResult";
import { useCrud } from "@/api/useCrud";
import { CheckHash } from "@/components/check_hash/CheckHash";
import { Alert } from "@/components/ui/Alert/Alert";
import { FileInput } from "@/components/ui/FileInput/FileInput";
import { Form } from "@/components/ui/Form/Form";
import { FormLayout } from "@/components/ui/Form/FormLayout";
import { t, tx } from "@/i18n/translate";
import type {
  ELECTION_IMPORT_VALIDATE_REQUEST_PATH,
  ElectionDefinitionValidateResponse,
} from "@/types/generated/openapi";
import { formatDateFull } from "@/utils/dateTime";
import { fileTooLargeError, isFileTooLarge } from "@/utils/uploadFileSize";
import { useElectionCreateContext } from "../hooks/useElectionCreateContext";

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: TODO function should be refactored
export function UploadElectionDefinition() {
  const { state, dispatch } = useElectionCreateContext();
  const navigate = useNavigate();
  const [error, setError] = useState<ReactNode | undefined>();
  const [file, setFile] = useState<File | undefined>();
  const createPath: ELECTION_IMPORT_VALIDATE_REQUEST_PATH = `/api/elections/import/validate`;
  const { create } = useCrud<ElectionDefinitionValidateResponse>({ createPath });

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO function should be refactored
  async function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const currentFile = e.target.files ? e.target.files[0] : undefined;
    if (currentFile !== undefined) {
      setFile(currentFile);
      if (await isFileTooLarge(currentFile)) {
        setError(fileTooLargeError(currentFile));
        return;
      }

      const data = await currentFile.text();
      const response = await create({ committee_category: "GSB", election_data: data });

      if (isSuccess(response)) {
        dispatch({
          type: "SELECT_ELECTION_DEFINITION",
          response: response.data,
          electionDefinitionData: data,
          electionDefinitionFileName: currentFile.name,
        });
        setError(undefined);
      } else if (isError(response)) {
        // Response code 413 indicates that the file is too large
        if (response instanceof ApiError && response.code === 413) {
          setError(fileTooLargeError(currentFile));
        } else {
          setError(
            tx("election.invalid_election_definition.description", {
              file: () => <strong>{currentFile.name}</strong>,
            }),
          );
        }
      }
    } else {
      setFile(undefined);
    }
  }

  if (
    state.election &&
    state.electionDefinitionFileName &&
    state.electionDefinitionRedactedHash &&
    state.electionDefinitionData &&
    !state.electionDefinitionHash
  ) {
    async function onSubmit(chunks: string[]) {
      const response = await create({
        committee_category: "GSB",
        election_data: state.electionDefinitionData,
        election_hash: chunks,
      });
      if (isSuccess(response)) {
        dispatch({
          type: "SET_ELECTION_DEFINITION_HASH",
          electionDefinitionHash: chunks,
        });
        await navigate("/elections/create/committee-category");
      } else if (isError(response) && response instanceof ApiError && response.reference === "InvalidHash") {
        setError(response.message);
      }
    }

    return (
      <CheckHash
        title={state.election.name}
        subtitle={formatDateFull(new Date(state.election.election_date))}
        header={t("election.check_eml.election_title")}
        description={tx("election.check_eml.election_description", {
          file: () => {
            return <strong>{state.electionDefinitionFileName}</strong>;
          },
        })}
        instructions={t("election.check_eml.check_hash_instructions")}
        errorTitle={t("election.check_eml.error.title")}
        errorDescription={t("election.check_eml.error.description")}
        redactedHash={state.electionDefinitionRedactedHash}
        error={error}
        onSubmit={(chunks) => void onSubmit(chunks)}
      />
    );
  }

  return (
    <section className="md">
      <Form title={t("election.import_election_eml")}>
        <FormLayout>
          <FormLayout.Section>
            {error && (
              <Alert type="error" title={t("election.invalid_election_definition.title")} inline>
                <p>{error}</p>
              </Alert>
            )}

            <p>{t("election.use_instructions_to_import_eml")}</p>

            <FileInput id="upload-eml" file={file} onChange={(e) => void onFileChange(e)}>
              {t("select_file")}
            </FileInput>
          </FormLayout.Section>
        </FormLayout>
      </Form>
    </section>
  );
}
